import { useState } from "react";
import { usePanelProps } from "@/context/panel-props-context";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import { ContentDiv, Section } from "../elements/card-group";
import QumFunctionalLayout from "./qum-functional-layout";

export default function MetadataDrawer() {
  const [activeTab, setActiveTab] = useState<string>("Blueprint");
  const { panelProps } = usePanelProps();

  // Extract metadata fields - qum might not exist for architecture components
  const metadata = panelProps?.metadata || {};
  const { name, blueprint, qum, ...restMetadata } = metadata;
  // Separate blueprint from qum - if blueprint doesn't exist, use all metadata except qum
  const blueprintData = blueprint || restMetadata;

  // Create ordered blueprint entries with name first
  const orderedBlueprint = { name, ...blueprintData };
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col h-full"
      >
        {/* Tab Navigation */}
        <div className="border-b border-surface-border px-4">
          <TabsList className="grid w-full grid-cols-2 bg-transparent">
            <TabsTrigger
              value="Blueprint"
              className="data-[state=active]:bg-surface-hover data-[state=active]:text-white"
            >
              Blueprint
            </TabsTrigger>
            <TabsTrigger
              value="Functional"
              className="data-[state=active]:bg-surface-hover data-[state=active]:text-white"
            >
              Functional
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          <TabsContent value="Blueprint" className="m-0 h-full">
            <div className={`h-full overflow-y-auto ${CUSTOM_SCROLLBAR}`}>
              <div className="p-4 space-y-4">
                {Object.entries(orderedBlueprint ?? {}).map(([key, value]) => (
                  <Section
                    title={key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                    key={key}
                  >
                    <div className="grid grid-cols-1 gap-2 text-left">
                      <ContentDiv
                        content={
                          typeof value === "object" ? (
                            <pre className="text-sm text-muted whitespace-pre-wrap overflow-auto font-mono">
                              {JSON.stringify(value, undefined, 2)}
                            </pre>
                          ) : (
                            String(value)
                          )
                        }
                      />
                    </div>
                  </Section>
                ))}

                {/* If no blueprint data available */}
                {(!orderedBlueprint ||
                  Object.keys(orderedBlueprint).length === 0) && (
                  <div className="text-center text-muted py-8">
                    <div className="text-lg">No blueprint data available</div>
                    <div className="text-sm">
                      This component doesn't contain blueprint metadata
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="Functional" className="m-0 h-full">
            {qum && Array.isArray(qum) && qum.length > 0 ? (
              <QumFunctionalLayout qumData={qum} />
            ) : (
              <div className="text-center text-muted py-8">
                <div className="text-lg">No functional data available</div>
                <div className="text-sm">
                  This component doesn't have linked functional data (QUM).
                  <br />
                  Functional data is managed separately in the Functional tab of
                  the Semantic Model.
                </div>
              </div>
            )}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
